import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaLock, FaSignOutAlt, FaArrowLeft, FaPalette, FaTimes, FaBell, FaBuilding, FaUsers } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import Button from '../common/Button';
import { enhancedTextContrast, enhancedHeadingContrast } from '../../styles/contrastUtils';
import ProfileTabs, { TabItem } from '../profile/ProfileTabs';
import PersonalInfoTab from '../profile/PersonalInfoTab';
import SecurityTab from '../profile/SecurityTab';
import CalendarSync from '../profile/CalendarSync';
import UsersTab from '../profile/UsersTab';
import AdminTab from '../profile/AdminTab';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  gap: 2rem;
  height: 100vh; /* Utiliser toute la hauteur de la fenêtre */
  overflow-y: auto; /* Ajouter un ascenseur vertical */
  
  /* Styles pour la scrollbar */
  scrollbar-width: thin; /* Pour Firefox */
  scrollbar-color: rgba(0, 0, 0, 0.3) transparent; /* Pour Firefox */
  
  /* Styles pour la scrollbar sur WebKit (Chrome, Safari, etc.) */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 6px;
  }
  
  /* Adaptation pour mobile */
  @media (max-width: 768px) {
    padding: 1rem;
    gap: 1rem;
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.border};
  }
`;

const PageTitle = styled.h1`
  font-size: 1.8rem;
  font-weight: 600;
  margin: 0;
`;

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  /* Adaptation pour mobile */
  @media (max-width: 768px) {
    padding: 1.5rem;
    gap: 1.5rem;
    margin-bottom: 2rem;
  }
  
  /* Adaptation pour les petits écrans */
  @media (max-height: 600px) {
    padding: 1rem;
    gap: 1rem;
  }
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Avatar = styled.div<{ $imageUrl?: string | null; $borderColor?: string | null; $initials?: string }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: ${({ theme, $imageUrl, $borderColor }) => $imageUrl ? 'transparent' : ($borderColor || theme.colors.primary)};
  background-image: ${({ $imageUrl }) => $imageUrl ? `url(${$imageUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
  position: relative;
  border: 3px solid ${({ $borderColor, theme }) => $borderColor || theme.colors.primary};
  overflow: visible; /* Permet au texte de déborder si nécessaire */
  z-index: 1; /* S'assure que le texte est au-dessus du fond */
  
  &::after {
    content: ${({ $initials }) => $initials ? `"${$initials}"` : '""'};
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    z-index: 2;
  }
`;

const ColorButton = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
  z-index: 10;
  
  &:hover {
    transform: scale(1.2);
    background-color: ${props => props.theme.colors.primaryDark};
  }
`;

const ColorPickerContainer = styled.div`
  position: absolute;
  top: 90px;
  left: 0;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 16px;
  z-index: 1001;
  width: 220px;
`;

const ColorPickerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const ColorPickerTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  color: ${props => props.theme.colors.text};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.colors.textLight};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 50%;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.backgroundLight};
    color: ${props => props.theme.colors.text};
  }
`;

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-bottom: 12px;
`;

const ColorOption = styled.div<{ $color: string; $isSelected: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${props => props.$color};
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid ${props => props.$isSelected ? props.theme.colors.primary : 'transparent'};
  
  &:hover {
    transform: scale(1.1);
  }
`;

const SaveButton = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  width: 100%;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.border};
    cursor: not-allowed;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const UserName = styled.h2`
  font-size: 1.5rem;
  margin: 0;
  color: #000000;
  
  /* Style spécifique pour Samsung Galaxy et autres appareils mobiles */
  @media (max-width: 480px) {
    ${enhancedHeadingContrast}
  }
`;

const UserEmail = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textLight};
  margin: 0;
  
  /* Style spécifique pour Samsung Galaxy et autres appareils mobiles */
  @media (max-width: 480px) {
    ${enhancedTextContrast}
  }
`;

const UserRole = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
  font-weight: 500;
  
  /* Style spécifique pour Samsung Galaxy et autres appareils mobiles */
  @media (max-width: 480px) {
    ${enhancedHeadingContrast}
  }
`;


const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  
  button {
    min-height: 44px;
    font-weight: 500;
    
    @media (max-width: 768px) {
      min-height: 48px;
      font-size: 16px;
    }
  }
`;


const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const { 
    supabaseUser, 
    signOut, 
    loading: authLoading
  } = useAuth();
  
  const { 
    userProfile, 
    updateUserProfile
  } = useUserProfile();
  
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [savingColor, setSavingColor] = useState(false);
  
  // Couleurs prédéfinies pour l'avatar
  const PREDEFINED_COLORS = [
    '#4CAF50', // Vert
    '#2196F3', // Bleu
    '#F44336', // Rouge
    '#FFC107', // Jaune
    '#9C27B0', // Violet
    '#FF9800', // Orange
    '#795548', // Marron
    '#607D8B', // Bleu-gris
    '#E91E63', // Rose
    '#00BCD4', // Cyan
    '#8BC34A', // Vert clair
    '#FF5722', // Orange foncé
    '#673AB7', // Violet foncé
    '#3F51B5', // Indigo
    '#009688', // Turquoise
  ];

  // Initialiser la couleur sélectionnée à partir du profil utilisateur
  useEffect(() => {
    if (userProfile?.color) {
      setSelectedColor(userProfile.color);
    }
  }, [userProfile]);
  
  // Fonction pour sélectionner une couleur
  const handleColorSelect = (color: string) => {
    setSelectedColor(color);
  };
  
  // Fonction pour sauvegarder la couleur sélectionnée
  const handleSaveColor = async () => {
    if (!userProfile || !selectedColor) return;
    
    try {
      setSavingColor(true);
      
      // Mettre à jour le profil utilisateur avec la nouvelle couleur
      await updateUserProfile({
        color: selectedColor
      });
      
      // Fermer le sélecteur de couleurs
      setIsColorPickerOpen(false);
      
      console.log('Couleur de l\'avatar mise à jour avec succès:', selectedColor);
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la couleur de l\'avatar:', error);
    } finally {
      setSavingColor(false);
    }
  };

  // Extraire l'initiale du nom d'utilisateur pour l'avatar
  const userInitial = userProfile?.first_name && userProfile?.name
    ? `${userProfile.first_name[0]}${userProfile.name[0]}`.toUpperCase()
    : supabaseUser?.email
      ? supabaseUser.email[0].toUpperCase()
      : 'U';

  // Gérer la déconnexion de l'utilisateur
  const handleSignOut = () => {
    signOut()
      .then(() => {
        // Redirection vers la page de connexion
        window.location.href = '/login';
      })
      .catch((error) => {
        console.error('Erreur lors de la déconnexion:', error);
      });
  };

  // Définir les onglets du profil
  const tabs: TabItem[] = [
    {
      id: 'personal',
      label: 'Profil',
      icon: <FaUser />,
      component: <PersonalInfoTab />,
      visible: true
    },
    {
      id: 'security',
      label: 'Sécurité',
      icon: <FaLock />,
      component: <SecurityTab />,
      visible: true
    },
    {
      id: 'calendar',
      label: 'Agenda',
      icon: <FaBell />,
      component: <CalendarSync />,
      visible: true
    },
    {
      id: 'users',
      label: 'Utilisateurs',
      icon: <FaUsers />,
      component: <UsersTab />,
      visible: userProfile?.is_owner === true
    },
    {
      id: 'admin',
      label: 'Paramètres',
      icon: <FaBuilding />,
      component: <AdminTab />,
      visible: userProfile?.is_owner === true
    }
  ];

  const handleBack = () => {
    navigate('/');
  };

  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={handleBack} aria-label="Retour">
          <FaArrowLeft />
        </BackButton>
        <PageTitle>
          {(() => {
            console.log('Rendu du titre:', { 
              userProfile, 
              name: userProfile?.name, 
              firstName: userProfile?.first_name 
            });
            return userProfile && (userProfile.first_name || userProfile.name) ? 
              `${userProfile.first_name || ''} ${userProfile.name || ''}` : 
              'Mon profil';
          })()}
        </PageTitle>
      </PageHeader>
      
      <ProfileContainer>
        <ProfileHeader>
          <div style={{ position: 'relative' }}>
            <Avatar 
              $imageUrl={userProfile?.photo_url}
              $borderColor={userProfile?.color || undefined}
              $initials={!userProfile?.photo_url ? userInitial : undefined}
            >
              <ColorButton onClick={() => setIsColorPickerOpen(!isColorPickerOpen)}>
                <FaPalette />
              </ColorButton>
            </Avatar>
            
            {/* Sélecteur de couleurs pour l'avatar */}
            {isColorPickerOpen && (
              <ColorPickerContainer>
                <ColorPickerHeader>
                  <ColorPickerTitle>Couleur de l'avatar</ColorPickerTitle>
                  <CloseButton onClick={() => setIsColorPickerOpen(false)}>
                    <FaTimes size={16} />
                  </CloseButton>
                </ColorPickerHeader>
                
                <ColorGrid>
                  {PREDEFINED_COLORS.map((color) => (
                    <ColorOption
                      key={color}
                      $color={color}
                      $isSelected={selectedColor === color}
                      onClick={() => handleColorSelect(color)}
                    />
                  ))}
                </ColorGrid>
                
                <SaveButton 
                  onClick={handleSaveColor} 
                  disabled={savingColor || !selectedColor}
                >
                  {savingColor ? 'Enregistrement...' : 'Enregistrer'}
                </SaveButton>
              </ColorPickerContainer>
            )}
          </div>
          <UserInfo>
            <UserName>
              {userProfile ? `${userProfile.first_name || ''} ${userProfile.name || ''}` : 'Mon profil'}
            </UserName>
            <UserEmail>{userProfile?.email || supabaseUser?.email}</UserEmail>
            {userProfile?.role && <UserRole>{userProfile.role}</UserRole>}
          </UserInfo>
        </ProfileHeader>

        {/* Système d'onglets */}
        <ProfileTabs tabs={tabs} defaultTab="personal" />

        {/* Bouton de déconnexion */}
        <ButtonsContainer style={{ marginTop: '2rem' }}>
          <Button 
            type="button" 
            $variant="danger" 
            onClick={handleSignOut}
            disabled={authLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontWeight: 'bold',
              fontSize: '16px',
              padding: '12px 16px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <FaSignOutAlt /> Se déconnecter
          </Button>
        </ButtonsContainer>
      </ProfileContainer>
    </PageContainer>
  );
};

export default UserProfile;

